import { useState, useRef, useEffect } from 'react'
import { User, Wallet, LogOut, ChevronDown } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { cn } from '../../lib/utils'

export function UserMenu() {
  const { user, wallets, isAuthenticated, isLoading, login, logout } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (isLoading) {
    return (
      <div className="h-8 w-8 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
    )
  }

  if (!isAuthenticated) {
    return (
      <button
        onClick={login}
        className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        Sign In
      </button>
    )
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        {user?.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt=""
            className="h-8 w-8 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 text-white">
            <User className="h-4 w-4" />
          </div>
        )}
        <ChevronDown
          className={cn(
            'h-4 w-4 text-slate-400 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
          {/* User Info */}
          <div className="border-b border-slate-200 p-4 dark:border-slate-700">
            <div className="font-medium text-slate-900 dark:text-white">
              {user?.name || user?.email}
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              {user?.email}
            </div>
          </div>

          {/* Wallets */}
          {wallets.length > 0 && (
            <div className="border-b border-slate-200 p-2 dark:border-slate-700">
              <div className="px-2 py-1.5 text-xs font-medium uppercase tracking-wider text-slate-400">
                Connected Wallets
              </div>
              {wallets.map((wallet) => (
                <div
                  key={wallet.id}
                  className="flex items-center gap-2 rounded-md px-2 py-2 text-sm"
                >
                  <Wallet className="h-4 w-4 text-slate-400" />
                  <span className="font-mono text-xs text-slate-700 dark:text-slate-300">
                    {wallet.address.slice(0, 6)}...{wallet.address.slice(-4)}
                  </span>
                  <span className="ml-auto rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                    {wallet.chain}
                  </span>
                  {wallet.isDefault && (
                    <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                      Default
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Logout */}
          <div className="p-2">
            <button
              onClick={() => {
                logout()
                setIsOpen(false)
              }}
              className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
